import React, { useState, useEffect, useMemo } from 'react';
import {ArrowUpDown, ArrowUp, ArrowDown} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MultiSelect from '../componets/MultiSelect';
import { getDadosLancamentos } from '../api';


const CustosDetalhados = () => {

const parseBRL = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;

  const str = String(value).trim();
  if (!str) return 0;

  // Remove todos os pontos (separadores de milhar) e substitui vírgula por ponto
  const limpo = str.replace(/[^\d,.-]/g, '')
                   .replace(/\./g, '')
                   .replace(',', '.');

  const numero = Number(limpo);
  return isNaN(numero) ? 0 : numero;
};

  const [dadosLancamentos, setDadosLancamentos] = useState([]);
  const [agrupado, setAgrupado] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandidos, setExpandidos] = useState({});
  const [error, setError] = useState(null);  
  const [filters, setFilters] = useState({
    projeto: [],
    categoria: [],
    empresa: [],
    conta: []
  });

  const [ordenacao, setOrdenacao] = useState({
    campo: null,
    direcao: 'asc'
  });

  const navigate = useNavigate();

  useEffect(() => {
    carregarLancamentos();
  }, []);

  const carregarLancamentos = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('📡 Buscando dados de lançamentos...');
      const result = await getDadosLancamentos();

      const dados = result.data
        .filter(item => item['Departamento'] === 'OPERAÇÃO')
        .map(item => {
          const dataCompleta = item['Data (completa)'] || '';
          let mes = null;
          let ano = null;

          if (dataCompleta.includes('/')) {
            const partes = dataCompleta.split('/');
            if (partes.length === 3) {
              mes = Number(partes[1]);
              ano = Number(partes[2]);
            }
          }
          return {
            empresa: item['Minha Empresa (Razão Social)'] || 'N/A',
            categoria: item['Categoria'] || 'N/A',
            conta: item['Conta Corrente'] || 'N/A',
            projeto: item['Projeto'] || 'N/A',
            valor: parseBRL(item['Valor'] || 0),
            departamento: item['Departamento'],
            data: dataCompleta,
            mes,
            ano
          };
        });

      console.log(`📊 Lançamentos processados: ${dados.length}`);

      setDadosLancamentos(dados);
    } catch (e) {
      console.error('❌ Erro:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ========= AGRUPAMENTO POR PROJETO =========
  useEffect(() => {
    const mapa = dadosLancamentos.reduce((acc, item) => {
      if (!acc[item.projeto]) acc[item.projeto] = [];
      acc[item.projeto].push(item);
      return acc;
    }, {});

    let agrupamentoInicial = Object.entries(mapa).map(([projeto, itens]) => ({
      projeto,
      itens,
      total: itens.reduce((s, i) => s + i.valor, 0)
    }));

    // ========= LÓGICA DE RATEIO =========
    const projetoRateio = agrupamentoInicial.find(p => p.projeto === 'RATEIO DE OPERAÇÕES');
    if (projetoRateio) {
      // Projetos que receberão o rateio (todos exceto o próprio RATEIO DE OPERAÇÕES)
      const projetosParaRatear = agrupamentoInicial.filter(p => p.projeto !== 'RATEIO DE OPERAÇÕES');
      
      // Calcula o total dos projetos (em valor absoluto para a proporção)
      const totalOutrosProjetos = projetosParaRatear.reduce((s, p) => s + Math.abs(p.total), 0);
      
      if (totalOutrosProjetos > 0) {
        // Distribui o valor do rateio proporcionalmente
        agrupamentoInicial = agrupamentoInicial.map(grupo => {
          if (grupo.projeto === 'RATEIO DE OPERAÇÕES') {
            return grupo; // Mantém o projeto de rateio inalterado
          }
          
          // Calcula a proporção deste projeto em relação ao total
          const proporcao = Math.abs(grupo.total) / totalOutrosProjetos;
          
          // Calcula o valor do rateio para este projeto
          const valorRateio = projetoRateio.total * proporcao;
          
          // Adiciona um item virtual representando o rateio
          const itemRateio = {
            empresa: 'RATEIO',
            categoria: 'Rateio de Operações',
            conta: 'Rateio Proporcional',
            projeto: grupo.projeto,
            valor: valorRateio,
            departamento: 'OPERAÇÃO',
            data: '',
            mes: null,
            ano: null,
            isRateio: true // Flag para identificar itens de rateio
          };
          
          return {
            ...grupo,
            itens: [...grupo.itens, itemRateio],
            total: grupo.total + valorRateio,
            valorRateio: valorRateio // Guarda o valor do rateio separadamente
          };
        });
      }
    }

    setAgrupado(agrupamentoInicial);
  }, [dadosLancamentos]);

  // ========= FILTRO E ORDENAÇÃO =========
  const filtradoEOrdenado = useMemo(() => {
    // Primeiro aplica os filtros
    let resultado = agrupado
      .map(p => ({
        ...p,
        itens: p.itens.filter(i => {
          const f1 = filters.projeto.length === 0 || filters.projeto.includes(p.projeto);
          const f2 = filters.categoria.length === 0 || filters.categoria.includes(i.categoria);
          const f3 = filters.empresa.length === 0 || filters.empresa.includes(i.empresa);
          const f4 = filters.conta.length === 0 || filters.conta.includes(i.conta);
          return f1 && f2 && f3 && f4;
        })
      }))
      .filter(p => p.itens.length > 0)
      .map(p => ({
        ...p,
        total: p.itens.reduce((s, i) => s + i.valor, 0)
      }));

    // Depois aplica a ordenação
    if (ordenacao.campo) {
      resultado.sort((a, b) => {
        let valorA, valorB;

        switch (ordenacao.campo) {
          case 'projeto':
            valorA = a.projeto.toLowerCase();
            valorB = b.projeto.toLowerCase();
            break;
          case 'registro':
            valorA = a.itens.length;
            valorB = b.itens.length;
            break;
          case 'total':
            valorA = a.total;
            valorB = b.total;
            break;
          default:
            return 0;
        }

        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return resultado;
  }, [agrupado, filters, ordenacao]);

  const totalProjetos = filtradoEOrdenado.length;
  const totalItens = filtradoEOrdenado.reduce((s, p) => s + p.itens.length, 0);
  const totalValor = filtradoEOrdenado.reduce((s, p) => s + p.total, 0);
  const valorRateio =  filtradoEOrdenado.filter(p => p.valorRateio).reduce((s, p) => s + p.valorRateio, 0) * -1

  const toggleExpand = (projeto) =>
    setExpandidos(prev => ({ ...prev, [projeto]: !prev[projeto] }));

  const limparFiltros = () =>
    setFilters({ projeto: [], categoria: [], empresa: [], conta: [] });

  const formatCurrency = (v) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const handleOrdenar = (campo) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc'
    }));
  };

  // ========= OPTIONS =========
  const optionsProjeto = [...new Set(agrupado.map(p => p.projeto))].map(p => ({ value: p, label: p }));
  const optionsCategoria = [...new Set(dadosLancamentos.map(i => i.categoria))].map(v => ({ value: v, label: v }));
  const optionsEmpresa = [...new Set(dadosLancamentos.map(i => i.empresa))].map(v => ({ value: v, label: v }));
  const optionsConta = [...new Set(dadosLancamentos.map(i => i.conta))].map(v => ({ value: v, label: v }));

  // ======================= COMPONENTE ICONE =======================
  const IconeOrdenacao = ({ campo, ordenacao, handleOrdenar }) => {
    return (
      <span
        onClick={() => handleOrdenar(campo)}
        className="cursor-pointer inline-flex items-center ml-1 select-none"
      >
        {ordenacao.campo !== campo && (
          <ArrowUpDown size={12} className="opacity-40" />
        )}
        {ordenacao.campo === campo && ordenacao.direcao === 'asc' && (
          <ArrowDown size={12} />
        )}
        {ordenacao.campo === campo && ordenacao.direcao === 'desc' && (
          <ArrowUp size={12} style={{ transform: 'rotate(0deg)' }} />
        )}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* HEADER */}
      <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <span className="text-2xl font-bold">YOUX</span>
          <span className="text-xl font-light">GROUP</span>
        </div>

          <button
            onClick={() => {
              navigate('/inicio');
            }}
            className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold"
          >
            Menu Principal
          </button>   

      </header>

      <main className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">
              Detalhamento de Custo
          </h1>
        </div>

        {/* CARDS RESUMO */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-white p-3 rounded shadow">
            <div className="text-xs text-gray-600">Projetos</div>
            <div className="text-2xl font-bold text-purple-700">{totalProjetos}</div>
          </div>
          <div className="bg-white p-3 rounded shadow">
            <div className="text-xs text-gray-600">Registros</div>
            <div className="text-2xl font-bold text-purple-700">{totalItens}</div>
          </div>
          <div className="bg-white p-3 rounded shadow">
            <div className="text-xs text-gray-600">Valor Rateado</div>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(valorRateio)}
            </div>
          </div>
          <div className="bg-white p-3 rounded shadow">
            <div className="text-xs text-gray-600">Total</div>
            <div className="text-2xl font-bold text-pink-700">{formatCurrency((totalValor*-1)-valorRateio)}</div>
          </div>
        </div>

        {/* FILTROS */}
        <div className="bg-white p-4 rounded shadow mb-6">
          <div className="grid grid-cols-4 gap-3 mb-3">
              <MultiSelect
                options={optionsProjeto}
                placeholder="Projetos"
                value={filters.projeto}
                onChange={(v) => setFilters(f => ({ ...f, projeto: v }))}
              />            
              <MultiSelect
                options={optionsCategoria}
                placeholder="Categorias"
                value={filters.categoria}
                onChange={(v) => setFilters(f => ({ ...f, categoria: v }))}

              />            
              <MultiSelect
                options={optionsEmpresa}
                placeholder="Empresas"
                value={filters.empresa}
                onChange={(v) => setFilters(f => ({ ...f, empresa: v }))}
              />           

              <MultiSelect
                options={optionsConta}
                placeholder="Contas"
                value={filters.conta}
                onChange={(v) => setFilters(f => ({ ...f, conta: v }))}
              />          </div>
          <button
            onClick={limparFiltros}
            className="text-sm text-pink-600 hover:text-pink-700 font-medium"
          >
            Limpar Filtros
          </button>
        </div>

        {/* TABELA */}
        <div className="bg-white p-4 rounded shadow">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Carregando...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">Erro: {error}</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-22">
                    Projeto
                    <IconeOrdenacao campo="projeto" ordenacao={ordenacao} handleOrdenar={handleOrdenar} />
                  </th>
                  <th className="text-center py-3 px-2">
                    Registros
                    <IconeOrdenacao campo="registro" ordenacao={ordenacao} handleOrdenar={handleOrdenar} />
                  </th>
                  <th className="text-right py-3 px-2">Custo Direto</th>
                  <th className="text-right py-3 px-2">Rateio</th>
                  <th className="text-right py-3 px-2">
                    Total
                    <IconeOrdenacao campo="total" ordenacao={ordenacao} handleOrdenar={handleOrdenar} />
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtradoEOrdenado.map(p => (
                  <React.Fragment key={p.projeto}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="py-2">{p.projeto}</td>
                      <td className="py-2 text-center">{p.itens.length}</td>
                      <td className="py-2 text-right">
                        {formatCurrency((p.total - (p.valorRateio || 0)) * -1)}
                      </td>
                      <td className="py-2 text-right text-blue-600">
                        {p.valorRateio ? formatCurrency(p.valorRateio * -1) : '-'}
                      </td>
                      <td className="py-2 text-right font-semibold">
                        {formatCurrency(p.total * -1)}
                      </td>
                      <td className="py-2 text-center">
                        <button onClick={() => toggleExpand(p.projeto)} className="text-indigo-600">
                          {expandidos[p.projeto] ? '▼' : '▶'}
                        </button>
                      </td>
                    </tr>
                    {expandidos[p.projeto] && (
                      <tr>
                        <td colSpan={6} className="bg-gray-50 p-3">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-1">Empresa</th>
                                <th className="text-left py-1">Categoria</th>
                                <th className="text-left py-1">Conta</th>
                                <th className="text-left py-1">Data</th>
                                <th className="text-right py-1">Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.itens.map((i, idx) => (
                                <tr 
                                  key={idx} 
                                  className={`border-t ${i.isRateio ? 'bg-blue-50 font-semibold' : ''}`}
                                >
                                  <td className="py-1">{i.empresa}</td>
                                  <td className="py-1">{i.categoria}</td>
                                  <td className="py-1">{i.conta}</td>
                                  <td className="py-1">{i.data}</td>
                                  <td className="text-right py-1">
                                    {formatCurrency(i.valor)}
                                    {i.isRateio && (
                                      <span className="ml-2 text-xs text-blue-600">(rateado)</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default CustosDetalhados;